import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { ApiQueryKey } from 'services/config/queryKey';
import { tradeList as getTradeList } from 'services/api/conversation';
import { queries } from 'services/queries';

/** 订单列表搜索参数，与 TradeSearchBar SearchParams 对齐 */
export type OrderListSearchParams = Partial<{
  external_trade_ids: string;
  trackingNumber: string;
  itemName: string;
  spuId: string;
  sku: string;
  sellerSku: string;
}>;

interface OrderListParams {
  buyerId: string;
  storeId: string;
  tunnelAccountId: string;
  pageSize: number;
  channel: Conversation.Conversation['channel'];
  /** 有值时走搜索接口，无值时走默认列表 */
  searchParams?: OrderListSearchParams;
}

/** 用于乐观更新的查询参数类型 */
export interface OrderQueryParams {
  internal_buyer_id: string;
  internal_store_id: string;
  tunnelAccountId: string;
  page_size: number;
}

/** 带有查询参数的订单类型，用于乐观更新 */
export type TradeWithQueryParams = Conversation.Trade.TradeWithLogistics & {
  _queryParams: OrderQueryParams;
};

export interface UseOrderListResult {
  hasNext?: boolean;
  total: number;
  orderList: TradeWithQueryParams[];
  isLoading: boolean;
  fetchNextPage: () => void;
  currentPage: number;
}

export const useOrderList = (params: OrderListParams) => {
  const { buyerId, storeId, tunnelAccountId, channel, pageSize, searchParams = {} } = params;

  // 仅保留有值的搜索条件传给接口
  const effectiveSearchParams = useMemo(() => {
    if (!searchParams || typeof searchParams !== 'object') return {};
    return Object.fromEntries(
      Object.entries(searchParams).filter(([, v]) => v != null && v !== ''),
    ) as OrderListSearchParams;
  }, [searchParams]);

  // 构建查询参数，用于乐观更新时定位正确的缓存
  const queryParams: OrderQueryParams = useMemo(
    () => ({
      internal_buyer_id: buyerId,
      internal_store_id: storeId,
      tunnelAccountId,
      page_size: pageSize,
      ...effectiveSearchParams,
    }),
    [buyerId, storeId, tunnelAccountId, pageSize, effectiveSearchParams],
  );

  const {
    hasNextPage,
    data: orderListRes,
    isLoading: isOrderListLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [ApiQueryKey.OrderList, queryParams],
    queryFn: ({ queryKey, pageParam, signal }) => {
      const params = queryKey[1] as OrderQueryParams;
      const pageNo = (pageParam as number | undefined) ?? 1;
      return getTradeList(
        {
          ...params,
          page_no: pageNo,
        },
        undefined,
        signal,
      );
    },
    getNextPageParam: (lastPage) => {
      const { pageNo, hasNext } = lastPage.pageInfo;
      if (hasNext) {
        return pageNo + 1;
      }
    },
  });

  const tradeList = useMemo(
    () =>
      orderListRes?.pages.flatMap((page) => page.tradeList)?.filter((order) => order !== undefined),
    [orderListRes],
  );

  const isLazada = channel === 'LAZADA';

  const internalTradeIds = useMemo(() => {
    return tradeList?.map((order) => order.internalTradeId) ?? [];
  }, [tradeList]);

  const lazadaLogisticsEnabled = isLazada && internalTradeIds.length > 0;
  const { data: lazadaLogisticsRes } = useQuery({
    ...queries.orders.lazadaLogistics({
      internal_trade_ids: internalTradeIds.join(','),
    }),
    enabled: lazadaLogisticsEnabled,
  });

  const logisticsEnabled = !isLazada && internalTradeIds.length > 0;
  const { data: logisticsRes } = useQuery({
    ...queries.orders.logistics({
      internal_trade_ids: internalTradeIds.join(','),
    }),
    enabled: logisticsEnabled,
  });
  const { data: eligibilityRes } = useQuery({
    ...queries.orders.eligibility({ internalTradeIds }),
    enabled: logisticsEnabled,
  });

  const getLazadaTrackingInfo = useCallback(
    (
      lazadaLogistics: Conversation.Trade.LazadaLogistics[] = [],
      thirdTrackingInfo: Conversation.Trade.ThirdLogistics[] = [],
    ) => {
      const tradeIdLazadaMap = lazadaLogistics.reduce<
        Record<string, Conversation.Trade.LazadaLogistics>
      >((acc, item) => {
        acc[item.internalTradeId] = item;
        return acc;
      }, {});
      const tradeIdThirdMap = thirdTrackingInfo.reduce<
        Record<string, Conversation.Trade.ThirdLogistics[]>
      >((acc, item) => {
        const id = item.internalTradeId;
        if (!acc[id]) acc[id] = [];
        acc[id].push(item);
        return acc;
      }, {});

      return {
        tradeIdLazadaMap,
        tradeIdThirdMap,
      };
    },
    [],
  );

  const getLogisticsTrackingInfo = useCallback(
    (
      logisticsData: Conversation.Trade.Logistics[] = [],
      thirdTrackingInfo: Conversation.Trade.ThirdLogistics[] = [],
    ) => {
      const tradeIdLogisticsListMap = logisticsData.reduce<
        Record<string, Conversation.Trade.Logistics[]>
      >((acc, item) => {
        const id = item.internalTradeId;
        if (!acc[id]) acc[id] = [];
        acc[id].push(item);
        return acc;
      }, {});
      const tradeIdThirdMap = thirdTrackingInfo.reduce<
        Record<string, Conversation.Trade.ThirdLogistics[]>
      >((acc, item) => {
        const id = item.internalTradeId;
        if (!acc[id]) acc[id] = [];
        acc[id].push(item);
        return acc;
      }, {});

      return {
        tradeIdLogisticsListMap,
        tradeIdThirdMap,
      };
    },
    [],
  );

  const orderList: TradeWithQueryParams[] = useMemo(() => {
    if (!tradeList?.length) return [];

    if (isLazada) {
      const { tradeIdLazadaMap, tradeIdThirdMap } = getLazadaTrackingInfo(
        lazadaLogisticsRes?.data?.data,
        lazadaLogisticsRes?.data?.thirdTrackingInfo,
      );
      return tradeList.map((order) => {
        const lazadaLogistics = tradeIdLazadaMap[order.internalTradeId];
        const thirdLogisticsList = tradeIdThirdMap[order.internalTradeId];
        return {
          ...order,
          ...(lazadaLogistics !== undefined && { lazadaLogistics }),
          ...(thirdLogisticsList !== undefined && { thirdLogisticsList }),
          _queryParams: queryParams,
        };
      });
    }

    const { tradeIdLogisticsListMap, tradeIdThirdMap } = getLogisticsTrackingInfo(
      logisticsRes?.data?.data,
      logisticsRes?.data?.thirdTrackingInfo,
    );

    return tradeList.map((order) => {
      const logisticsList = tradeIdLogisticsListMap[order.internalTradeId];
      const thirdLogisticsList = tradeIdThirdMap[order.internalTradeId];
      const mercadoLogistics = logisticsList !== undefined ? logisticsList[0] : undefined;
      return {
        eligibility: eligibilityRes?.orderEligibilityList.find(
          (_) => _.orderId === order.externalTradeId,
        ),
        ...order,
        ...(logisticsList !== undefined && channel !== 'MERCADO' && { logisticsList }),
        ...(thirdLogisticsList !== undefined && { thirdLogisticsList }),
        ...(channel === 'MERCADO' && mercadoLogistics !== undefined && { mercadoLogistics }),
        _queryParams: queryParams,
      };
    });
  }, [
    tradeList,
    isLazada,
    logisticsRes,
    eligibilityRes,
    lazadaLogisticsRes,
    getLogisticsTrackingInfo,
    getLazadaTrackingInfo,
    queryParams,
    channel,
  ]);

  const total = useMemo(() => {
    return orderListRes?.pages[orderListRes.pages.length - 1]?.pageInfo?.totalCount ?? 0;
  }, [orderListRes]);

  const currentPage = useMemo(() => {
    return orderListRes?.pages[orderListRes.pages.length - 1]?.pageInfo?.pageNo ?? 1;
  }, [orderListRes]);

  return {
    hasNext: hasNextPage,
    total,
    orderList,
    isLoading: isOrderListLoading,
    fetchNextPage,
    currentPage,
  };
};
